import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Package, Users, CurrencyDollar, Storefront } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ProductsTab from "../components/admin/ProductsTab";
import OrdersTab from "../components/admin/OrdersTab";
import CategoriesTab from "../components/admin/CategoriesTab";
import EmailsTab from "../components/admin/EmailsTab";
import DiscountsTab from "../components/admin/DiscountsTab";

const STAT_CONFIGS = [
  { label: "Products", key: "total_products", icon: Storefront },
  { label: "Orders", key: "total_orders", icon: Package },
  { label: "Customers", key: "total_users", icon: Users },
  { label: "Revenue", key: "total_revenue", icon: CurrencyDollar, format: v => `$${(v || 0).toFixed(2)}` },
];

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailLogs, setEmailLogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [discounts, setDiscounts] = useState([]);

  useEffect(() => {
    if (!user || user === false || user.role !== "admin") {
      navigate("/auth");
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, productsRes, ordersRes, emailsRes, catsRes, discountsRes] = await Promise.all([
        API.get("/admin/stats"),
        API.get("/products"),
        API.get("/orders"),
        API.get("/admin/emails").catch(() => ({ data: [] })),
        API.get("/admin/categories").catch(() => ({ data: [] })),
        API.get("/admin/discounts").catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      setEmailLogs(Array.isArray(emailsRes.data) ? emailsRes.data : []);
      setCategories(Array.isArray(catsRes.data) ? catsRes.data : []);
      setDiscounts(Array.isArray(discountsRes.data) ? discountsRes.data : []);
    } catch (err) {
      console.error("Failed to load admin data:", err);
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="admin-page">
      <div className="flex items-center justify-between mb-12">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3 font-medium">Administration</p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#FDFBF7] tracking-tighter">Dashboard</h1>
        </div>
        <button onClick={handleLogout} className="border border-white/10 px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-[#A1A1AA] hover:text-[#FDFBF7] transition-all duration-300 font-light" data-testid="admin-logout-btn">Sign Out</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12" data-testid="admin-stats">
        {STAT_CONFIGS.map(({ label, key, icon: Icon, format }) => (
          <div key={label} className="bg-[#121212] border border-[rgba(212,175,55,0.1)] p-6">
            <Icon size={20} weight="thin" className="text-[#D4AF37] mb-3" />
            <p className="text-xs text-[#A1A1AA] uppercase tracking-[0.15em] mb-1 font-medium">{label}</p>
            <p className="text-2xl text-[#FDFBF7] font-['Cormorant_Garamond'] font-light">
              {loading ? "..." : format ? format(stats[key]) : stats[key]}
            </p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="products">
        <TabsList className="bg-transparent border-b border-white/5 rounded-none w-full justify-start gap-8 h-auto pb-0">
          <TabsTrigger value="products" className="bg-transparent text-[#A1A1AA] data-[state=active]:text-[#D4AF37] data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] pb-3 text-xs uppercase tracking-[0.15em] font-medium" data-testid="admin-products-tab">Products</TabsTrigger>
          <TabsTrigger value="orders" className="bg-transparent text-[#A1A1AA] data-[state=active]:text-[#D4AF37] data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] pb-3 text-xs uppercase tracking-[0.15em] font-medium" data-testid="admin-orders-tab">Orders</TabsTrigger>
          <TabsTrigger value="emails" className="bg-transparent text-[#A1A1AA] data-[state=active]:text-[#D4AF37] data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] pb-3 text-xs uppercase tracking-[0.15em] font-medium" data-testid="admin-emails-tab">Emails</TabsTrigger>
          <TabsTrigger value="categories" className="bg-transparent text-[#A1A1AA] data-[state=active]:text-[#D4AF37] data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] pb-3 text-xs uppercase tracking-[0.15em] font-medium" data-testid="admin-categories-tab">Categories</TabsTrigger>
          <TabsTrigger value="discounts" className="bg-transparent text-[#A1A1AA] data-[state=active]:text-[#D4AF37] data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] pb-3 text-xs uppercase tracking-[0.15em] font-medium" data-testid="admin-discounts-tab">Discounts</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-8">
          <ProductsTab products={products} categories={categories} onReload={loadData} />
        </TabsContent>

        <TabsContent value="orders" className="mt-8">
          <OrdersTab orders={orders} onReload={loadData} />
        </TabsContent>

        <TabsContent value="emails" className="mt-8">
          <EmailsTab emailLogs={emailLogs} />
        </TabsContent>

        <TabsContent value="categories" className="mt-8">
          <CategoriesTab categories={categories} onReload={loadData} />
        </TabsContent>

        <TabsContent value="discounts" className="mt-8">
          <DiscountsTab discounts={discounts} onReload={loadData} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
